import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, PenTool, Share2, UserCheck, Clock, Eye } from 'lucide-react';

const ProgressTracker = ({ document = {} }) => {
  const steps = [
    {
      id: 'created',
      title: 'Created',
      icon: FileText,
      description: 'Document setup',
      isCompleted: !!document,
      date: document?.created_at
    },
    {
      id: 'edited',
      title: 'Content Added',
      icon: PenTool,
      description: 'Content prepared',
      isCompleted: document?.content && document.content.length > 0,
      date: document?.updated_at
    },
    {
      id: 'published',
      title: 'Published',
      icon: Share2,
      description: 'Ready to sign',
      isCompleted: !!document?.shareable_link,
      date: document?.published_at,
    },
    {
      id: 'awaiting_signature',
      title: 'Awaiting',
      icon: Clock,
      description: 'Shared with signer',
      isCompleted: document?.shareable_link && !document?.signed_by,
      date: document?.published_at
    },
    {
      id: 'opened_first_time',
      title: 'Opened',
      icon: Eye,
      description: 'Recipient opened',
      date: document?.first_opened_at,
    },
    {
      id: 'signed',
      title: 'Signed',
      icon: UserCheck,
      description: 'Completed',
      isCompleted: !!document?.signed_by,
      date: document?.signed_at
    }
  ];

  const getCurrentStepIndex = () => {
    if (document?.signed_by) return 5;
    if (document?.first_opened_at) return 4;
    if (document?.shareable_link && !document?.first_opened_at) return 3;
    if (document?.shareable_link) return 2;
    if (document?.content && document.content.length > 0) return 1;
    return 0;
  };
  const currentStepIndex = getCurrentStepIndex();

  // Split into two rows: first 3 left-to-right, last 3 right-to-left
  const topRow = steps.slice(0, 3);
  const bottomRow = [...steps.slice(3)].reverse(); // reversed for right-to-left display

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, when: "beforeChildren", staggerChildren: 0.08 }
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 }
  };

  const pulseVariants = {
    pulse: {
      scale: [1, 1.15, 1],
      opacity: [0.6, 1, 0.6],
      transition: { duration: 2, repeat: Infinity, ease: "easeInOut" }
    }
  };

  const StepNode = ({ step, index, isCurrent, isCompleted }) => {
    const StepIcon = step.icon;
    return (
      <motion.div
        variants={stepVariants}
        className="flex flex-col items-center relative flex-1 min-w-0"
      >
        {/* Circle */}
        <motion.div
          animate={isCompleted ? { scale: [1, 1.15, 1] } : {}}
          transition={{ duration: 0.3 }}
          className={`
            relative w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center z-10
            ${isCompleted || isCurrent
              ? 'bg-gradient-to-br from-blue-500 to-blue-400 shadow-md shadow-blue-200 dark:shadow-blue-900/40'
              : 'bg-gray-200 dark:bg-gray-700'}
            ${isCurrent ? 'ring-[3px] ring-blue-200 dark:ring-blue-800' : ''}
          `}
        >
          <StepIcon
            className={`w-4 h-4 sm:w-5 sm:h-5 ${
              isCompleted || isCurrent ? 'text-white' : 'text-gray-400 dark:text-gray-500'
            }`}
          />
          {isCurrent && (
            <motion.div
              variants={pulseVariants}
              animate="pulse"
              className="absolute inset-0 rounded-full bg-blue-400 -z-10"
            />
          )}
        </motion.div>

        {/* Title */}
        <p className={`mt-1.5 text-[11px] sm:text-xs font-medium text-center leading-tight
          ${isCompleted || isCurrent ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}
        `}>
          {step.title}
        </p>

        {/* Timestamp or description */}
        {step.date ? (
          <div className="text-center mt-0.5">
            <p className="text-[9px] sm:text-[10px] text-blue-500 dark:text-blue-400 leading-tight tabular-nums font-medium">
              {new Date(step.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </p>
            <p className="text-[8px] sm:text-[9px] text-blue-400/70 dark:text-blue-500/70 leading-tight tabular-nums">
              {new Date(step.date).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </p>
          </div>
        ) : (
          <p className="text-[9px] sm:text-[10px] text-gray-300 dark:text-gray-600 text-center leading-tight mt-0.5">
            {step.description}
          </p>
        )}
      </motion.div>
    );
  };

  // SVG snake path connecting all 6 steps
  // Layout: 3 across top, curve right-side down, 3 across bottom (reversed)
  // We use a viewBox-based SVG that stretches to fit
  const SnakePath = () => {
    // Progress: 0-5 maps to path segments
    const totalSegments = 5; // 5 connections between 6 nodes
    const progressFraction = currentStepIndex / totalSegments;

    return (
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 300 120"
        preserveAspectRatio="none"
        fill="none"
      >
        {/* Background path (gray) */}
        <path
          d="M 25 28 L 150 28 L 275 28 Q 295 28 295 48 L 295 72 Q 295 92 275 92 L 150 92 L 25 92"
          stroke="currentColor"
          className="text-gray-200 dark:text-gray-700"
          strokeWidth="2"
          fill="none"
          vectorEffect="non-scaling-stroke"
        />
        {/* Animated progress path (blue) */}
        <motion.path
          d="M 25 28 L 150 28 L 275 28 Q 295 28 295 48 L 295 72 Q 295 92 275 92 L 150 92 L 25 92"
          stroke="url(#progressGradient)"
          strokeWidth="2.5"
          fill="none"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: progressFraction }}
          transition={{ duration: 1, ease: "easeInOut", delay: 0.3 }}
        />
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#60a5fa" />
          </linearGradient>
        </defs>
      </svg>
    );
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="w-full bg-white dark:bg-darkbg-800 rounded-lg shadow-md p-4 sm:p-6 mb-8"
    >
      <motion.h3
        variants={stepVariants}
        className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 sm:mb-5 text-center"
      >
        Document Progress
      </motion.h3>

      {/* Snake layout container */}
      <div className="relative mx-auto max-w-md">
        <SnakePath />

        {/* Top row: steps 0-2, left to right */}
        <div className="relative flex justify-between px-2 sm:px-4 pb-3">
          {topRow.map((step, i) => {
            const globalIndex = i;
            return (
              <StepNode
                key={step.id}
                step={step}
                index={globalIndex}
                isCurrent={globalIndex === currentStepIndex}
                isCompleted={globalIndex < currentStepIndex}
              />
            );
          })}
        </div>

        {/* Spacer for the curve */}
        <div className="h-4 sm:h-5" />

        {/* Bottom row: steps 3-5, right to left (reversed visually) */}
        <div className="relative flex justify-between px-2 sm:px-4 pt-3">
          {bottomRow.map((step) => {
            const globalIndex = steps.findIndex(s => s.id === step.id);
            return (
              <StepNode
                key={step.id}
                step={step}
                index={globalIndex}
                isCurrent={globalIndex === currentStepIndex}
                isCompleted={globalIndex < currentStepIndex}
              />
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default ProgressTracker;
